import React, {
  createContext, memo, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import {
  Box, Chip, CircularProgress, IconButton, Paper, TextField, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ConnectorPreparationDagNode from './ConnectorPreparationDagNode';
import ProjectWorkspaceDagNode from './ProjectWorkspaceDagNode';
import { PROJECT_WORKSPACE_NODE_HEIGHT, PROJECT_WORKSPACE_NODE_WIDTH } from './projectDagNodeDimensions';
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import { motion, useReducedMotion } from 'motion/react';
import WorkflowActionNode from '../WorkflowActionNode';
import ActionTrackingBubble from './ActionTrackingBubble';
import datasourceFallbackIcon from '../../images/icon_datasource.svg';
import { ACTION_ICON_FALLBACK } from '../../Utils/actionIcons';
import { continuesAfterSuccess } from '../../Utils/actionFlowDecision';
import { readActionFunnelStage } from '../../Utils/actionFunnel';
import { actionRecoveryMessage, dagRecoveryInProgress } from '../../Utils/actionRecoveryFeedback';
import { formatTokenCount, getFlowTokenUsage } from '../../Utils/workflowTokenUsage';
import {
  DAG_HOVER_SHADOW,
  DAG_HOVER_TRANSFORM,
  DAG_HOVER_TRANSITION,
} from '../../Utils/dagInteractionStyles';

const NODE_WIDTH = 154;

export const DagInteractionContext = createContext(false);
export const DagEntranceContext = createContext(null);
export const DagNodeInternalsContext = createContext(null);
export const DagEffectsContext = createContext(false);
export const DagBuildFocusContext = createContext(null);

const DagLoadingOverlay = ({
  testId,
  ariaLabel,
  side = 'right',
  offset = 30,
  top = 43,
  transform = 'translate(50%, -50%)',
}) => (
  <Box
    data-testid={`${testId}-overlay`}
    className="nodrag nopan"
    onPointerDown={(event) => event.stopPropagation()}
    sx={{
      position: 'absolute',
      [side]: offset,
      top,
      width: 32,
      height: 32,
      transform,
      borderRadius: '50%',
      backgroundColor: '#f8f8ff',
      border: '2px solid #fff',
      boxShadow: '0 3px 10px rgba(65, 65, 140, 0.28)',
      display: 'grid',
      placeItems: 'center',
      zIndex: 32,
      pointerEvents: 'auto',
    }}
  >
    <CircularProgress
      aria-label={ariaLabel}
      data-testid={testId}
      size={28}
      thickness={4.5}
      sx={{ color: '#5757b0' }}
    />
  </Box>
);

const DagBuildHud = ({ label, t }) => (
  <Box
    data-testid="dag-action-creating-hud"
    sx={{
      position: 'absolute',
      top: -45,
      left: '50%',
      transform: 'translateX(-50%)',
      minWidth: 132,
      minHeight: 38,
      px: 1,
      py: 0.65,
      border: '1px solid rgba(157, 164, 255, 0.86)',
      borderRadius: 1.5,
      backgroundColor: 'rgba(28, 28, 76, 0.96)',
      backgroundImage: 'linear-gradient(rgba(157, 164, 255, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(157, 164, 255, 0.08) 1px, transparent 1px)',
      backgroundSize: '9px 9px',
      color: '#eef0ff',
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      boxShadow: '0 7px 22px rgba(32, 32, 104, 0.42), 0 0 0 3px rgba(101, 101, 189, 0.14)',
      pointerEvents: 'none',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '50%',
        bottom: -6,
        width: 10,
        height: 10,
        backgroundColor: '#24245f',
        borderRight: '1px solid rgba(157, 164, 255, 0.86)',
        borderBottom: '1px solid rgba(157, 164, 255, 0.86)',
        transform: 'translateX(-50%) rotate(45deg)',
      },
    }}
  >
    <Box
      sx={{
        position: 'relative',
        width: 25,
        height: 26,
        flexShrink: 0,
      }}
    >
      <GavelRoundedIcon
        data-testid="dag-action-creating-hammer"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: -1,
          left: 0,
          fontSize: 25,
          color: '#ffd166',
          filter: 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.45))',
          transformOrigin: '78% 82%',
          animation: 'dagActionHammerStrike 1.05s cubic-bezier(0.22, 1, 0.36, 1) infinite',
          '@keyframes dagActionHammerStrike': {
            '0%, 100%': { transform: 'rotate(24deg) translate(1px, -1px)' },
            '44%': { transform: 'rotate(24deg) translate(1px, -1px)' },
            '58%': { transform: 'rotate(-28deg) translate(-1px, 2px)' },
            '72%': { transform: 'rotate(-7deg) translate(0, 1px)' },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
      <Box
        data-testid="dag-action-creating-impact"
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: 0,
          width: 19,
          height: 7,
          border: '2px solid #ffd166',
          borderRadius: '50%',
          transform: 'translateX(-50%) scale(0.35)',
          opacity: 0,
          animation: 'dagActionHammerImpact 1.05s ease-out infinite',
          '@keyframes dagActionHammerImpact': {
            '0%, 49%': { transform: 'translateX(-50%) scale(0.35)', opacity: 0 },
            '60%': { transform: 'translateX(-50%) scale(0.7)', opacity: 0.9 },
            '100%': { transform: 'translateX(-50%) scale(1.25)', opacity: 0 },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        component="div"
        sx={{
          color: '#ffd166',
          fontSize: 9,
          fontWeight: 900,
          letterSpacing: '0.16em',
          lineHeight: 1,
        }}
      >
        {t('dag.action_building', 'BUILDING')}
      </Typography>
      <Box
        data-testid="dag-action-creating-progress"
        sx={{
          position: 'relative',
          height: 4,
          mt: 0.55,
          overflow: 'hidden',
          borderRadius: 999,
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
        }}
      >
        <Box
          sx={{
            width: '42%',
            height: '100%',
            borderRadius: 'inherit',
            background: 'linear-gradient(90deg, #7e8cff, #ffd166)',
            animation: 'dagActionBuildProgress 1.6s ease-in-out infinite',
            '@keyframes dagActionBuildProgress': {
              '0%': { transform: 'translateX(-120%)', opacity: 0.5 },
              '55%': { opacity: 1 },
              '100%': { transform: 'translateX(270%)', opacity: 0.7 },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      </Box>
      <Typography
        component="div"
        sx={{
          mt: 0.35,
          maxWidth: 91,
          overflow: 'hidden',
          color: 'rgba(238, 240, 255, 0.78)',
          fontSize: 8,
          fontWeight: 700,
          lineHeight: 1,
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
    <Box
      data-testid="dag-action-creating-sparks"
      sx={{
        position: 'absolute',
        left: 21,
        bottom: -3,
        width: 25,
        height: 18,
        pointerEvents: 'none',
      }}
    >
      {[0, 1, 2, 3].map((spark) => (
        <Box
          component="span"
          key={spark}
          sx={{
            position: 'absolute',
            left: 10,
            bottom: 4,
            width: 3,
            height: 7,
            borderRadius: 999,
            backgroundColor: '#ffd166',
            transform: `rotate(${(spark * 32) - 48}deg) translateY(-${spark % 2 ? 4 : 1}px)`,
            transformOrigin: 'bottom center',
            animation: `dagActionBuildSpark 1.05s ${spark * 0.1}s ease-out infinite`,
            '@keyframes dagActionBuildSpark': {
              '0%, 45%': { opacity: 0, scale: 0.4 },
              '60%': { opacity: 1, scale: 1 },
              '100%': { opacity: 0, scale: 0.55, translate: '0 -8px' },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      ))}
    </Box>
  </Box>
);

const DagActionMutationOverlay = ({ kind, ariaLabel, label, t }) => (
  <Box
    data-testid={`dag-action-${kind}-overlay`}
    role="status"
    aria-label={ariaLabel}
    className="nodrag nopan"
    onPointerDown={(event) => event.stopPropagation()}
    onClick={(event) => event.stopPropagation()}
    sx={{
      position: 'absolute',
      inset: -7,
      zIndex: 30,
      borderRadius: 2.5,
      border: kind === 'deleting'
        ? '2px dashed rgba(196, 63, 79, 0.62)'
        : '2px dashed rgba(87, 87, 176, 0.65)',
      backgroundColor: kind === 'deleting'
        ? 'rgba(255, 246, 247, 0.88)'
        : 'rgba(247, 247, 255, 0.88)',
      color: kind === 'deleting' ? '#b7354a' : '#5757b0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.7,
      pointerEvents: 'auto',
      overflow: 'visible',
      transformOrigin: 'center',
      boxShadow: kind === 'deleting'
        ? '0 5px 16px rgba(196, 63, 79, 0.18)'
        : '0 5px 16px rgba(87, 87, 176, 0.18)',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: -8,
        borderRadius: 'inherit',
        border: kind === 'deleting'
          ? '2px solid rgba(196, 63, 79, 0.42)'
          : '2px solid rgba(87, 87, 176, 0.42)',
        pointerEvents: 'none',
        animation: kind === 'deleting'
          ? 'dagActionDeleteRing 1.2s ease-out infinite'
          : 'dagActionCreateRing 1.8s cubic-bezier(0.22, 1, 0.36, 1) infinite',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: '-35%',
        width: '28%',
        background: kind === 'deleting'
          ? 'linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.75), transparent)'
          : 'linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.95), transparent)',
        transform: 'skewX(-18deg)',
        pointerEvents: 'none',
        animation: 'dagActionMutationShine 1.65s ease-in-out infinite',
      },
      ...(kind === 'creating' ? {
        backgroundImage: 'linear-gradient(rgba(101, 101, 189, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(101, 101, 189, 0.08) 1px, transparent 1px)',
        backgroundSize: '13px 13px',
      } : {}),
      '& > *': { position: 'relative', zIndex: 1 },
      '@keyframes dagActionCreateRing': {
        '0%': { transform: 'scale(0.84)', opacity: 0.9 },
        '65%, 100%': { transform: 'scale(1.16)', opacity: 0 },
      },
      '@keyframes dagActionDeleteRing': {
        '0%, 100%': { transform: 'scale(0.96)', opacity: 0.1 },
        '45%': { transform: 'scale(1.08)', opacity: 0.8 },
      },
      '@keyframes dagActionMutationShine': {
        '0%, 25%': { left: '-35%', opacity: 0 },
        '45%': { opacity: 0.9 },
        '70%, 100%': { left: '120%', opacity: 0 },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&::before, &::after': { animation: 'none' },
      },
    }}
  >
    {kind === 'creating' ? (
      <DagBuildHud label={label} t={t} />
    ) : (
      <CircularProgress size={17} thickness={5} sx={{ color: 'currentColor' }} />
    )}
    <Typography
      component="span"
      sx={{ fontSize: { xs: '8px', md: '9px' }, fontWeight: 800, lineHeight: 1.1 }}
    >
      {label}
    </Typography>
  </Box>
);

export const EditableDagNodeLabel = ({
  value,
  onSave,
  t,
  ariaLabel,
  testId = 'dag-flow-node-label',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const beginEditing = (event) => {
    if (typeof onSave !== 'function') return;
    event.stopPropagation();
    setDraft(String(value || ''));
    setEditing(true);
  };

  const discard = () => {
    setEditing(false);
    setDraft('');
  };

  const save = () => {
    const trimmedName = draft.trim();
    if (trimmedName && trimmedName !== String(value || '').trim()) {
      onSave(trimmedName);
    }
    discard();
  };

  if (editing) {
    return (
      <TextField
        autoFocus
        data-canvas-interactive="true"
        variant="standard"
        size="small"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={save}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Enter') {
            event.preventDefault();
            save();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            discard();
          }
        }}
        inputProps={{
          maxLength: 255,
          'aria-label': ariaLabel || t('workflow.automation_flow_name_label', 'Flow name'),
        }}
        sx={{
          width: '95%',
          px: 0.5,
          borderRadius: 0.75,
          backgroundColor: '#fbfbff',
          '& .MuiInputBase-input': {
            textAlign: 'center',
            color: '#30306a',
            fontSize: { xs: '10px', md: '12px' },
            fontWeight: 600,
          },
        }}
      />
    );
  }

  return (
    <Typography
      data-canvas-interactive="true"
      data-testid={testId}
      onClick={beginEditing}
      variant="body2"
      sx={{
        width: 'fit-content',
        maxWidth: 150,
        px: 0.75,
        py: 0.35,
        borderRadius: 1,
        color: '#30306a',
        fontWeight: 600,
        fontSize: { xs: '10px', md: '12px' },
        lineHeight: 1.2,
        textAlign: 'center',
        display: 'inline-block',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        cursor: typeof onSave === 'function' ? 'text' : 'default',
        backgroundColor: '#f4f4fc',
        border: '1px solid rgba(87, 87, 176, 0.24)',
        transition: 'background-color 160ms ease, border-color 160ms ease',
        '&:hover': typeof onSave === 'function' ? {
          backgroundColor: '#eeedff',
          borderColor: 'rgba(87, 87, 176, 0.48)',
        } : {},
      }}
    >
      {value}
    </Typography>
  );
};

export const resolveDagNodeStatus = (error, runtime) => {
  if (dagRecoveryInProgress(runtime)) return { state: 'repairing', count: 0 };
  const runtimeStatus = String(runtime?.status || '').toLowerCase();
  const processing = Number(runtime?.inProgress || 0);
  const completed = Number(runtime?.completed || 0);
  const failed = Number(runtime?.failed || 0);
  const skipped = Number(runtime?.skipped || 0);

  // A new retry supersedes a prior-run error, while a configuration error
  // still replaces stale pending or successful snapshots.
  if (runtimeStatus === 'queued') {
    return { state: 'queued', count: 0 };
  }
  if (runtimeStatus === 'processing' || runtimeStatus === 'running') {
    return { state: 'processing', count: processing };
  }
  if (runtime?.errorKind !== 'configuration' && completed > 0 && failed > 0) {
    return { state: 'partial', count: 0 };
  }
  if (error?.message || runtimeStatus === 'failed' || runtimeStatus === 'error' || failed > 0) {
    return { state: 'error', count: failed, message: error?.message || '' };
  }
  if (runtimeStatus === 'skipped') {
    return { state: 'skipped', count: skipped };
  }
  if (runtimeStatus === 'pending') {
    return { state: 'pending', count: 0 };
  }
  if (runtimeStatus === 'completed' || runtimeStatus === 'success') {
    return skipped > 0 && completed === 0
      ? { state: 'skipped', count: skipped }
      : { state: 'success', count: completed };
  }
  return null;
};

const NodeStatusIndicator = ({
  label, error, runtime, t, position = null, onOpenJournal = null,
  disableHover = false,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const reduceEffects = useContext(DagEffectsContext);
  const reduceMotion = prefersReducedMotion || reduceEffects;
  const status = resolveDagNodeStatus(error, runtime);
  if (!status) return null;

  const stateStyles = {
    repairing: {
      backgroundColor: '#7153ad', color: '#fff', boxShadow: '0 3px 10px rgba(90, 63, 145, 0.3)',
      focusOutline: 'rgba(113, 83, 173, 0.28)',
    },
    error: {
      backgroundColor: '#d14343',
      color: '#fff',
      boxShadow: '0 3px 10px rgba(145, 35, 35, 0.32)',
      focusOutline: 'rgba(209, 67, 67, 0.28)',
    },
    processing: {
      backgroundColor: '#3f6fd9',
      color: '#fff',
      boxShadow: '0 3px 10px rgba(48, 84, 168, 0.3)',
      focusOutline: 'rgba(63, 111, 217, 0.25)',
    },
    queued: {
      backgroundColor: '#5757b0',
      color: '#fff',
      boxShadow: '0 3px 10px rgba(69, 67, 154, 0.28)',
      focusOutline: 'rgba(87, 87, 176, 0.25)',
    },
    pending: {
      backgroundColor: '#777783',
      color: '#fff',
      boxShadow: '0 3px 10px rgba(70, 70, 82, 0.25)',
      focusOutline: 'rgba(119, 119, 131, 0.25)',
    },
    success: {
      backgroundColor: '#36a269',
      color: '#fff',
      boxShadow: '0 3px 10px rgba(35, 122, 68, 0.28)',
      focusOutline: 'rgba(54, 162, 105, 0.25)',
    },
    partial: {
      backgroundColor: '#d6a328',
      color: '#fff',
      boxShadow: '0 3px 10px rgba(160, 112, 16, 0.3)',
      focusOutline: 'rgba(214, 163, 40, 0.28)',
    },
    skipped: {
      backgroundColor: '#b7791f',
      color: '#fff',
      boxShadow: '0 3px 10px rgba(154, 95, 16, 0.28)',
      focusOutline: 'rgba(183, 121, 31, 0.25)',
    },
  }[status.state];
  const stateLabel = status.state === 'repairing' ? t('action_recovery.friendly_working') : status.state === 'processing'
    ? `${status.count} ${t('dag.status_processing', 'processing')}`
    : status.state === 'queued'
      ? t('dag.status_queued', 'queued')
      : status.state === 'pending'
        ? t('dag.status_pending', 'pending')
        : status.state === 'success'
          ? (status.count > 0
            ? `${status.count} ${t('dag.status_completed', 'completed')}`
            : t('dag.status_completed', 'completed'))
          : status.state === 'skipped'
            ? `${status.count} ${t('dag.status_skipped', 'skipped')}`
            : status.state === 'partial'
              ? t('dag.journal_partially_completed', 'Partially completed')
              : t('dag.node_error_title', 'Needs attention');
  const summary = [
    `${Number(runtime?.inProgress || 0)} ${t('dag.status_processing', 'processing')}`,
    `${Number(runtime?.completed || 0)} ${t('dag.status_completed', 'completed')}`,
    `${Number(runtime?.failed || 0)} ${t('dag.journal_failed', 'failed')}`,
    `${Number(runtime?.skipped || 0)} ${t('dag.status_skipped', 'skipped')}`,
  ].join(' \u00b7 ');
  const accessibleLabel = status.state === 'error' && error?.message
    ? t('dag.node_error_aria', '{{name}} has an error: {{message}}', {
      name: label,
      message: error.message,
    })
    : `${label}: ${stateLabel}`;
  const tooltip = status.state === 'repairing' ? actionRecoveryMessage(runtime.recovery, t) : status.state === 'error'
    ? (
      <Box sx={{ maxWidth: 320, py: 0.25 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 800 }}>
          {t('dag.node_error_title', 'Needs attention')}
        </Typography>
        {error?.message && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
            {error.message}
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
          {summary}
        </Typography>
      </Box>
    )
    : summary;
  const statusIcon = {
    repairing: <CircularProgress size={14} thickness={6} sx={{ color: 'currentColor' }} />,
    processing: <CircularProgress size={14} thickness={6} sx={{ color: 'currentColor' }} />,
    queued: <HourglassEmptyRoundedIcon sx={{ fontSize: 16 }} />,
    pending: <HourglassEmptyRoundedIcon sx={{ fontSize: 16 }} />,
    success: <CheckCircleRoundedIcon sx={{ fontSize: 17 }} />,
    partial: <WarningAmberRoundedIcon sx={{ fontSize: 17 }} />,
    skipped: <SkipNextRoundedIcon sx={{ fontSize: 17 }} />,
    error: <ErrorOutlineRoundedIcon sx={{ fontSize: 17 }} />,
  }[status.state];

  return (
    <Tooltip
      arrow
      placement="top"
      disableHoverListener={disableHover}
      disableFocusListener={disableHover}
      disableTouchListener={disableHover}
      title={tooltip}
    >
      <Box
        component="button"
        type="button"
        aria-label={accessibleLabel}
        data-testid="dag-node-status"
        data-node-state={status.state}
        className="nodrag nopan"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onOpenJournal?.();
        }}
        sx={{
          position: 'absolute',
          top: position?.top ?? 17,
          right: position?.right ?? 14,
          zIndex: 8,
          minWidth: 25,
          width: status.count > 0 ? 'auto' : 25,
          height: 25,
          px: status.count > 0 ? 0.55 : 0,
          borderRadius: 999, border: '2px solid #fff',
          backgroundColor: stateStyles.backgroundColor, color: stateStyles.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 0.25, boxShadow: stateStyles.boxShadow,
          overflow: 'visible',
          cursor: onOpenJournal ? 'pointer' : 'default',
          transition: DAG_HOVER_TRANSITION,
          '&:hover': onOpenJournal ? {
            transform: 'translateY(-1px) scale(1.04)',
            boxShadow: '0 6px 16px rgba(69, 67, 154, 0.32)',
          } : {},
          '&:focus-visible': { outline: `3px solid ${stateStyles.focusOutline}`, outlineOffset: 2 },
        }}
      >
        {status.state === 'processing' && !reduceMotion && (
          <motion.span
            key={'processing-ripple-' + (runtime?.activitySequence || 0)}
            data-testid="dag-status-ripple"
            aria-hidden="true"
            initial={{ opacity: 0.42, scale: 0.82 }}
            animate={{ opacity: 0, scale: 2.1 }}
            transition={{ duration: 0.76, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: -2,
              border: '2px solid rgba(255, 255, 255, 0.88)',
              borderRadius: '50%',
              pointerEvents: 'none',
            }}
          />
        )}
        <motion.span
          key={'status-icon-' + status.state}
          aria-hidden="true"
          initial={reduceMotion ? false : { opacity: 0, scale: 0.58, y: 2 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 620, damping: 23 }}
          style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
        >
          {statusIcon}
        </motion.span>
        {status.count > 0 && (
          <motion.span
            key={'status-count-' + status.state + '-' + status.count}
            aria-hidden="true"
            initial={reduceMotion ? false : { opacity: 0, y: 4, scale: 0.62 }}
            animate={{ opacity: 1, y: 0, scale: reduceMotion ? 1 : [0.76, 1.16, 1] }}
            transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 650, damping: 22, mass: 0.55 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'inherit',
              fontSize: 11,
              fontWeight: 800,
              lineHeight: 1,
              transformOrigin: 'center',
            }}
          >
            {status.count}
          </motion.span>
        )}
      </Box>
    </Tooltip>
  );
};

const NodeHandles = ({ node, actionControls, t, buildHighlight = false, animateBuild = false }) => {
  const kind = node.kind;
  const fallbackAnchor = {
    input: { left: 37, right: 37, top: 65 },
    action: { left: 37, right: 37, top: 65 },
    center: { left: 0, right: 0, top: PROJECT_WORKSPACE_NODE_HEIGHT / 2 },
    output: { left: 43, right: 43, top: 26 },
    add: { left: 37, right: 37, top: 40 },
  }[kind] || { left: 37, right: 37, top: 65 };
  const targetAnchor = node.handleAnchors?.target || {
    x: fallbackAnchor.left,
    y: fallbackAnchor.top,
    position: Position.Left,
  };
  const sourceAnchor = node.handleAnchors?.source || {
    x: (kind === 'center' ? PROJECT_WORKSPACE_NODE_WIDTH : NODE_WIDTH) - fallbackAnchor.right,
    y: fallbackAnchor.top,
    position: Position.Right,
  };
  const actionType = String(node.item?.type || '').trim().toLowerCase();
  const isFlowCall = kind === 'action' && ['triggerflow', 'looptriggerflow'].includes(actionType);
  const hasDecisionBranches = kind === 'action' && ['condition', 'humaninloop'].includes(actionType);
  // Keep the call's exit away from the continuation's add-action control,
  // including when a manually placed following action points downward.
  const flowCallAbove = sourceAnchor.y > fallbackAnchor.top;
  const flowCallSourceAnchor = {
    x: NODE_WIDTH / 2,
    y: fallbackAnchor.top + (flowCallAbove ? -40 : 40),
    position: flowCallAbove ? Position.Top : Position.Bottom,
  };
  const buildAnchor = kind === 'output' ? { x: NODE_WIDTH / 2, y: 26 } : sourceAnchor;
  const anchorStyle = (handleAnchor) => ({
    left: handleAnchor.x,
    right: 'auto',
    top: handleAnchor.y,
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
  });
  const isBranchingAction = kind === 'action'
    && (String(node.item?.type || '').trim().toLowerCase() === 'humaninloop'
      || (String(node.item?.type || '').trim().toLowerCase() === 'condition'
        && !continuesAfterSuccess(node.item?.data)));
  const canAddAction = Boolean(
    actionControls?.canManage
    && node.nodeType === 'action'
    && !isBranchingAction
  );
  const canAddFromCenter = Boolean(
    actionControls?.canManage
    && node.kind === 'center'
  );
  const tutorialStep = actionControls?.draftTutorialStep;
  const addActionLabel = t('dag.add_action_after', 'Add action after this stage');
  const tutorialTargetStyle = {
    boxShadow: '0 0 0 5px rgba(87, 87, 176, 0.2), 0 5px 14px rgba(55, 55, 120, 0.35)',
    outline: '2px solid rgba(87, 87, 176, 0.42)',
    outlineOffset: 2,
  };
  const humanOutcomeTargetHandles = {
    left: { position: Position.Left, x: 43, y: [16, 36] },
    right: { position: Position.Right, x: 111, y: [16, 36] },
    top: { position: Position.Top, x: [67, 87], y: 0 },
    bottom: { position: Position.Bottom, x: [67, 87], y: 52 },
  };
  const activateHandle = (event, callback) => {
    event.preventDefault();
    event.stopPropagation();
    callback?.();
  };
  const handleAddAction = (event) => activateHandle(
    event,
    () => actionControls?.onAddAfter?.(node)
  );
  const renderAddHandle = ({
    side,
    label,
    onActivate,
    tutorialTarget = false,
  }) => (
    <Box
      className="nodrag nopan dag-add-node-handle"
      aria-label={label}
      data-tutorial-target={tutorialTarget ? 'connector' : undefined}
      role="button"
      tabIndex={0}
      title={label}
      onPointerDown={(event) => activateHandle(event, onActivate)}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') activateHandle(event, onActivate);
      }}
      sx={{
        ...addActionHandleStyle,
        position: 'absolute',
        [side]: fallbackAnchor[side],
        top: fallbackAnchor.top,
        transform: `${side === 'left' ? 'translate(-50%, -50%)' : 'translate(50%, -50%)'}`,
        ...(tutorialTarget ? tutorialTargetStyle : {}),
      }}
    >
      <AddIcon className="dag-add-node-icon" sx={{ fontSize: 15 }} />
    </Box>
  );
  return (
    <>
      {!['input', 'system'].includes(kind) && (
        <Handle
          id="target"
          type="target"
          position={targetAnchor.position}
          isConnectable={false}
          style={{
            ...(['action', 'output'].includes(kind) ? hiddenScopeHandleStyle : handleStyle),
            ...anchorStyle(targetAnchor),
          }}
        />
      )}
      {kind === 'output' && ['approved', 'rejected'].flatMap((outcome, outcomeIndex) => (
        Object.entries(humanOutcomeTargetHandles).map(([side, anchor]) => {
          const x = Array.isArray(anchor.x) ? anchor.x[outcomeIndex] : anchor.x;
          const y = Array.isArray(anchor.y) ? anchor.y[outcomeIndex] : anchor.y;
          return (
            <Handle
              key={`${outcome}-${side}`}
              id={`human-outcome-${outcome}-target-${side}`}
              type="target"
              position={anchor.position}
              isConnectable={false}
              style={{
                ...hiddenScopeHandleStyle,
                left: x,
                right: 'auto',
                top: y,
                bottom: 'auto',
                transform: 'translate(-50%, -50%)',
              }}
            />
          );
        })
      ))}
      {isFlowCall && (
        <Handle
          id="flow-call-source"
          data-testid="dag-flow-call-source"
          type="source"
          position={flowCallSourceAnchor.position}
          isConnectable={false}
          style={{ ...hiddenScopeHandleStyle, ...anchorStyle(flowCallSourceAnchor) }}
        />
      )}
      {hasDecisionBranches && Object.entries({
        left: { x: 37, y: 65, position: Position.Left },
        right: { x: 117, y: 65, position: Position.Right },
        top: { x: 77, y: 25, position: Position.Top },
        bottom: { x: 77, y: 105, position: Position.Bottom },
      }).map(([side, anchor]) => (
        <Handle key={side} id={`branch-source-${side}`} type="source" position={anchor.position}
          isConnectable={false} style={{ ...hiddenScopeHandleStyle, ...anchorStyle(anchor) }} />
      ))}
      {buildHighlight && <Box
        role="img"
        aria-label={t('dag.flow_assistant.canvas.build_here', 'New steps are being prepared here')}
        data-testid="dag-build-focus"
        sx={{
          position: 'absolute', left: buildAnchor.x, top: buildAnchor.y,
          transform: 'translate(-50%, -50%)', width: 44, height: 44,
          borderRadius: '50%', pointerEvents: 'none', zIndex: 30,
          '&::before': {
            content: '""', position: 'absolute', inset: 0, borderRadius: '50%',
            border: '2px solid #7867df', backgroundColor: 'rgba(120,103,223,.10)',
            boxShadow: '0 0 0 6px rgba(120,103,223,.12), 0 0 24px 8px rgba(120,103,223,.36)',
            animation: animateBuild ? 'dagBuildFocusGlow 1.8s ease-in-out infinite' : 'none',
          },
          '@keyframes dagBuildFocusGlow': {
            '0%, 100%': { opacity: 0.72, transform: 'scale(.94)' },
            '50%': { opacity: 1, transform: 'scale(1.08)' },
          },
          '@media (prefers-reduced-motion: reduce)': { '&::before': { animation: 'none' } },
        }}
      />}
      {kind !== 'add' && (
        <>
          <Handle
            id="source"
            type="source"
            position={sourceAnchor.position}
            isConnectable={false}
            style={{
              ...(kind === 'output' || isBranchingAction
                ? hiddenScopeHandleStyle
                : canAddAction
                  ? { ...addActionHandleStyle, pointerEvents: 'none' }
                  : handleStyle),
              ...anchorStyle(sourceAnchor),
            }}
          />
          {canAddAction && (
            <Box
              className="nodrag nopan dag-add-node-handle dag-add-action-handle"
              aria-label={addActionLabel}
              role="button"
              tabIndex={0}
              title={addActionLabel}
              onPointerDown={handleAddAction}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') handleAddAction(event);
              }}
              sx={{
                ...addActionHandleStyle,
                position: 'absolute',
                ...anchorStyle(sourceAnchor),
                ...(kind === 'output' ? { opacity: 0, '&:hover, &:focus-visible': { opacity: 1 } } : {}),
              }}
            >
              <AddIcon className="dag-add-node-icon" sx={{ fontSize: 15 }} />
            </Box>
          )}
        </>
      )}
      {canAddFromCenter && renderAddHandle({
        side: 'left',
        label: t('dag.add_connector', 'Add connector'),
        onActivate: actionControls?.onAddConnector,
        tutorialTarget: tutorialStep === 'connector',
      })}
      {canAddFromCenter && renderAddHandle({
        side: 'right',
        label: t('dag.add_flow', 'Add flow'),
        onActivate: actionControls?.onAddFlow,
      })}
    </>
  );
};

const handleStyle = {
  width: 14,
  height: 14,
  zIndex: 18,
  border: '2px solid #ffffff',
  background: '#6565bd',
  boxShadow: '0 1px 4px rgba(55,55,120,.32)',
};

const hiddenScopeHandleStyle = {
  width: 24,
  height: 24,
  zIndex: 1,
  border: 'none',
  background: 'transparent',
  opacity: 0,
  pointerEvents: 'none',
};

const addActionHandleStyle = {
  width: 20,
  height: 20,
  zIndex: 24,
  borderRadius: '50%',
  border: '2px solid #ffffff',
  background: '#6565bd',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 7px rgba(55,55,120,.36)',
  cursor: 'pointer',
  pointerEvents: 'auto',
};

export const DagNode = ({
  id, label, kind, iconSrc = '', isEnabled = true, error = null, runtime = null,
  item = null, actionMeta = null, actionControls = null, search = null,
  inputLoading = false, outputLoading = false, flowState = null, actionCount = 0,
  flowCollapsed = false,
  tagCount = null,
  receivedCount = null,
  reanalysis = null,
  disableHover = false,
  connectorDisplayName = '',
  locale = undefined, flowSummary = '', t = (_key, fallback) => fallback,
}) => {
  const isSearchActive = Boolean(search?.active);
  const isSearchMatch = Boolean(search?.match);
  const flowAddPointerRef = useRef(null);
  if (kind === 'preparation') {
    return <ConnectorPreparationDagNode id={id} label={label} item={item} isEnabled={isEnabled}
      search={search} disableHover={disableHover} canManage={Boolean(actionControls?.canManage)}
      controls={actionControls} StatusIndicator={NodeStatusIndicator} />;
  }
  if (kind === 'add') {
    return (
      <Box sx={{ position: 'relative', width: NODE_WIDTH, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8 }}>
        <Paper
          elevation={1}
          sx={{
            width: 80, height: 80, borderRadius: '50%', border: '3px dashed rgba(87, 87, 176, 0.4)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', color: '#5757b0',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(87, 87, 176, 0.15)', transition: DAG_HOVER_TRANSITION,
            '&:hover': disableHover ? {} : {
              transform: DAG_HOVER_TRANSFORM,
              borderColor: '#5757b0',
              boxShadow: DAG_HOVER_SHADOW,
            },
          }}
        >
          <AddIcon sx={{ fontSize: 42 }} />
        </Paper>
        <Typography variant="body2" sx={{ color: '#30306a', fontWeight: 600, textAlign: 'center' }}>{label}</Typography>
      </Box>
    );
  }

  if (kind === 'center') {
    const centerStatus = resolveDagNodeStatus(error, runtime);
    const centerIsActive = Boolean(
      inputLoading
      || outputLoading
      || reanalysis?.pending
      || ['processing', 'running', 'queued'].includes(reanalysis?.runtime?.status)
      || ['processing', 'queued'].includes(centerStatus?.state)
    );
    return (
      <Box sx={{ position: 'relative', width: PROJECT_WORKSPACE_NODE_WIDTH }}>
        <ProjectWorkspaceDagNode label={label} tagCount={tagCount} receivedCount={receivedCount} active={centerIsActive}
          reanalysis={reanalysis} actionControls={actionControls} t={t} />
        {inputLoading && (
          <DagLoadingOverlay
            testId="dag-input-loading"
            ariaLabel={t('dag.loading_connectors', 'Loading connectors')}
            side="left"
            offset={0}
            top={PROJECT_WORKSPACE_NODE_HEIGHT / 2}
            transform="translate(-50%, -50%)"
          />
        )}
        {outputLoading && (
          <DagLoadingOverlay
            testId="dag-output-loading"
            ariaLabel={t('dag.loading_outputs', 'Loading flows and actions')}
            offset={0}
            top={PROJECT_WORKSPACE_NODE_HEIGHT / 2}
          />
        )}
      </Box>
    );
  }

  if (kind === 'output') {
    const normalizedFlowState = ['loading', 'ready', 'empty', 'error'].includes(flowState)
      ? flowState
      : (outputLoading ? 'loading' : (Number(actionCount) > 0 ? 'ready' : 'empty'));
    const normalizedActionCount = Math.max(Number(actionCount) || 0, 0);
    const canAddAction = Boolean(
      actionControls?.canManage && typeof actionControls?.onAddAfter === 'function'
    );
    const canToggleFlow = Boolean(
      normalizedActionCount > 0
      && typeof actionControls?.onToggleFlowCollapse === 'function'
    );
    const flowNode = {
      id,
      key: `flow:${id}`,
      item,
      label,
      kind,
      nodeType: 'flow',
    };
    const flowStateLabel = {
      loading: t('dag.flow_loading', 'Loading actions…'),
      ready: normalizedActionCount === 1
        ? t('dag.flow_ready_one', '1 action')
        : t('dag.flow_ready_other', '{{count}} actions', { count: normalizedActionCount }),
      empty: t('dag.flow_empty', 'No actions configured'),
      error: t('dag.flow_error', 'Unable to load flow actions'),
    }[normalizedFlowState];
    const flowStateColor = {
      loading: '#5757b0',
      ready: '#328d5d',
      empty: '#777789',
      error: '#c43f4f',
    }[normalizedFlowState];
    const handleAddAction = (event) => {
      event.stopPropagation();
      if (flowAddPointerRef.current?.dragged) {
        flowAddPointerRef.current = null;
        return;
      }
      flowAddPointerRef.current = null;
      if (!canAddAction) return;
      actionControls?.onAddAfter?.(flowNode);
    };
    const trackAddPointer = (event) => {
      if (event.button !== 0) return;
      flowAddPointerRef.current = { x: event.clientX, y: event.clientY, dragged: false };
    };
    const trackAddDrag = (event) => {
      const pointer = flowAddPointerRef.current;
      if (pointer && Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 5) {
        pointer.dragged = true;
      }
    };
    const handleToggleFlow = (event) => {
      event.preventDefault();
      event.stopPropagation();
      actionControls?.onToggleFlowCollapse?.(id);
    };
    const flowTokenUsage = getFlowTokenUsage(item);
    const flowTokenCountLabel = formatTokenCount(flowTokenUsage.total_tokens, locale);
    const flowSummaryText = flowSummary || t(
      'workflow.flow_summary_empty',
      'No actions are configured in this flow yet.'
    );
    return (
      <Tooltip
        arrow
        placement="top"
        disableInteractive
        disableHoverListener={disableHover}
        disableFocusListener={disableHover}
        disableTouchListener={disableHover}
        title={(
          <Box sx={{ maxWidth: 300 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {t('workflow.flow_summary_title', 'Flow summary')}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, lineHeight: 1.35 }}>
              {flowSummaryText}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.82 }}>
              {t('workflow.ai_token_usage_flow', 'AI tokens: {{count}}', {
                count: flowTokenCountLabel
              })}
            </Typography>
          </Box>
        )}
      >
        <Box sx={{
          position: 'relative',
          width: NODE_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.25,
          opacity: isSearchActive && !isSearchMatch ? 0.38 : 1,
          transform: isSearchActive && isSearchMatch ? 'translateY(-2px)' : 'none',
          transition: 'opacity 180ms ease, transform 180ms ease',
        }}>
        <Box
          role="button"
          tabIndex={canAddAction ? 0 : -1}
          aria-disabled={!canAddAction}
          aria-label={t('dag.add_action_after', 'Add action after this stage')}
          title={t('dag.add_action_after', 'Add action after this stage')}
          data-testid="dag-flow-add-action"
          data-tutorial-target={actionControls?.draftTutorialStep === 'action' && normalizedFlowState === 'empty' ? 'action' : undefined}
          onPointerDown={trackAddPointer}
          onPointerMove={trackAddDrag}
          onClick={handleAddAction}
          onKeyDown={(event) => {
            if (canAddAction && ['Enter', ' '].includes(event.key)) {
              event.preventDefault();
              handleAddAction(event);
            }
          }}
          sx={{
            width: 36,
            height: 36,
            mt: 1,
            borderRadius: '50%',
            border: '3px solid #fff',
            backgroundColor: '#6565bd',
            color: '#fff',
            boxShadow: '0 2px 7px rgba(55,55,120,.36)',
            display: 'grid',
            placeItems: 'center',
            cursor: 'grab',
            position: 'relative',
            zIndex: 24,
            transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), background-color 180ms ease, box-shadow 180ms ease',
            '&:active': { cursor: 'grabbing' },
            '&:hover, &:focus-visible': {
              transform: 'translateY(-1px) scale(1.04)',
              backgroundColor: '#5050aa',
              boxShadow: '0 4px 12px rgba(55,55,120,.42)',
              outline: '3px solid rgba(101,101,189,.18)',
            },
          }}
        >
          <AddIcon sx={{ fontSize: 19 }} />
        </Box>
        {(outputLoading || normalizedFlowState === 'loading') && (
          <DagLoadingOverlay
            testId="dag-flow-loading"
            ariaLabel={t('dag.loading_outputs', 'Loading flows and actions')}
            offset={43}
            top={26}
          />
        )}
        <Box sx={{
          width: '100%',
          maxWidth: 150,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.35,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, minHeight: 20 }}>
            <Typography
              component="span"
              data-testid="dag-flow-state"
              variant="caption"
              role={normalizedFlowState === 'error' ? 'alert' : 'status'}
              aria-live="polite"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.3,
                color: flowStateColor,
                fontSize: { xs: '8px', md: '9px' },
                fontWeight: 600,
                lineHeight: 1.2,
                textAlign: 'center',
              }}
            >
              {normalizedFlowState === 'loading' && <CircularProgress size={10} thickness={6} sx={{ color: 'currentColor' }} />}
              {normalizedFlowState === 'ready' && <CheckCircleRoundedIcon sx={{ fontSize: 12 }} />}
              {normalizedFlowState === 'error' && <ErrorOutlineRoundedIcon sx={{ fontSize: 12 }} />}
              {flowStateLabel}
            </Typography>
            {canToggleFlow && (
              <IconButton
                className="nodrag nopan"
                data-testid={`dag-flow-collapse-${id}`}
                aria-label={flowCollapsed
                  ? t('dag.expand_flow', 'Expand flow')
                  : t('dag.collapse_flow', 'Collapse flow')}
                title={flowCollapsed
                  ? t('dag.expand_flow', 'Expand flow')
                  : t('dag.collapse_flow', 'Collapse flow')}
                size="small"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={handleToggleFlow}
                sx={{
                  p: 0.15,
                  color: '#5757b0',
                  '&:hover': { backgroundColor: 'rgba(87,87,176,.1)' },
                }}
              >
                {flowCollapsed
                  ? <ExpandMoreRoundedIcon sx={{ fontSize: 16 }} />
                  : <ExpandLessRoundedIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            )}
          </Box>
        </Box>
        </Box>
      </Tooltip>
    );
  }

  const isAction = kind === 'action';
  const pendingMutation = isAction && ['creating', 'deleting'].includes(actionMeta?.pendingMutation)
    ? actionMeta.pendingMutation
    : null;
  const canManageNode = Boolean(actionControls?.canManage && !pendingMutation);
  const canManageAction = Boolean(isAction && actionControls?.canManage && !pendingMutation);
  const canDeleteInput = Boolean(kind === 'input' && canManageNode
    && Number.isInteger(Number(item?.datasource?.id)) && Number(item.datasource.id) > 0
    && typeof actionControls?.onRequestDeleteInput === 'function');
  const nodeStatus = resolveDagNodeStatus(error, runtime);
  const scopeIndicatorAnchor = isAction ? { left: '0%', top: '0%' } : null;
  const trackingLabel = isAction ? readActionFunnelStage(item?.customData ?? item?.data)?.label?.trim() : '';
  const setting = isAction
    ? {
      ...(item || {}),
      id,
      name: label,
      iconSrc: iconSrc || item?.iconSrc || ACTION_ICON_FALLBACK,
    }
    : {
      id: item?.datasource?.id ?? id,
      type: kind,
      name: label,
      iconSrc: iconSrc || datasourceFallbackIcon,
      scope: 'project',
      connectorType: connectorDisplayName || item?.type,
      datasource: item?.datasource,
    };
  const actionControlStyles = {
    controlButton: {
      color: '#5757b0',
      '&:hover': { backgroundColor: 'rgba(87, 87, 176, 0.1)' },
    },
    deleteButton: {
      color: '#c43f4f',
      '&:hover': { backgroundColor: 'rgba(196, 63, 79, 0.1)' },
    },
  };
  return (
    <Box sx={{
      position: 'relative',
      width: NODE_WIDTH,
      '& > div': { mb: 0 },
      ...(nodeStatus?.state === 'error' ? { filter: 'drop-shadow(0 0 5px rgba(209, 67, 67, 0.22))' } : {}),
      ...(pendingMutation === 'creating' ? {
        animation: 'dagActionCreateEntrance 1.55s cubic-bezier(0.22, 1, 0.36, 1) infinite',
        '@keyframes dagActionCreateEntrance': {
          '0%': {
            transform: 'translateY(5px) scale(0.94)',
            opacity: 0.58,
            filter: 'drop-shadow(0 0 0 rgba(87, 87, 176, 0))',
          },
          '38%': {
            transform: 'translateY(-2px) scale(1.025)',
            opacity: 1,
            filter: 'drop-shadow(0 0 11px rgba(87, 87, 176, 0.52))',
          },
          '68%, 100%': {
            transform: 'translateY(0) scale(1)',
            opacity: 1,
            filter: 'drop-shadow(0 0 5px rgba(87, 87, 176, 0.18))',
          },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      } : {}),
      ...(pendingMutation === 'deleting' ? {
        animation: 'dagActionDeleteExit 1.2s ease-in-out infinite',
        '@keyframes dagActionDeleteExit': {
          '0%, 100%': {
            transform: 'rotate(0deg) scale(1)',
            opacity: 0.78,
            filter: 'grayscale(0) drop-shadow(0 0 0 rgba(196, 63, 79, 0))',
          },
          '28%': {
            transform: 'rotate(-1.3deg) scale(1.018)',
            opacity: 0.94,
            filter: 'grayscale(0.18) drop-shadow(0 0 9px rgba(196, 63, 79, 0.42))',
          },
          '58%': {
            transform: 'rotate(1.3deg) scale(0.98)',
            opacity: 0.58,
            filter: 'grayscale(0.72) drop-shadow(0 0 7px rgba(196, 63, 79, 0.32))',
          },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      } : {}),
    }}>
      <WorkflowActionNode
        setting={setting}
        index={canManageAction ? (actionMeta?.index ?? 0) : 0}
        totalSettings={canManageAction ? (actionMeta?.totalSettings ?? 1) : 1}
        isEnabled={isEnabled}
        isSelected={false}
        status="idle"
        togglingScope={canManageAction && actionControls.togglingScopeId === id}
        showScopeIndicator={isAction}
        scopeIndicatorPosition="left"
        scopeIndicatorAnchor={scopeIndicatorAnchor}
        showEnableToggle={canManageNode}
        separateEnableToggle
        showNodePointer={false}
        solidNameBackground
        onToggleEnable={() => (
          isAction
            ? actionControls?.onToggleEnable?.(setting)
            : actionControls?.onToggleInputEnable?.(item)
        )}
        onToggleScope={() => actionControls?.onRequestScopeChange?.(setting)}
        onClick={undefined}
        onConfigure={canManageNode ? () => actionControls?.onConfigureNode?.({ id, kind, nodeType: isAction ? 'action' : 'connector', item, label }) : undefined}
        onEditLogic={canManageNode && actionControls?.onEditNodeLogic
          ? () => actionControls.onEditNodeLogic({ id, kind, nodeType: isAction ? 'action' : 'connector', item, label })
          : undefined}
        onToggleTracking={canManageAction && item?.scope !== 'project' && actionControls?.onToggleTracking
          ? () => actionControls.onToggleTracking(setting) : undefined}
        trackingEnabled={Boolean(trackingLabel)}
        trackingPending={String(actionControls?.trackingPendingId || '') === String(id)}
        trackingBlocked={Boolean(actionControls?.trackingPendingId) && String(actionControls.trackingPendingId) !== String(id)}
        onOpenData={() => actionControls?.onOpenJournal?.({ nodeType: isAction ? 'action' : 'connector',
          nodeId: isAction ? id : item?.datasource?.id, label, journalMode: isAction ? 'node' : 'connector-node' })}
        onDelete={canDeleteInput ? () => actionControls.onRequestDeleteInput(item)
          : canManageAction ? () => actionControls?.onRequestDelete?.(setting) : undefined}
        onUngroup={canManageAction ? actionControls?.onUngroup : undefined}
        onToggleGroupMode={canManageAction ? actionControls?.onToggleGroupMode : undefined}
        onNameSave={isAction
          ? (canManageAction ? actionControls?.onRename : undefined)
          : (canManageNode && item?.datasource?.id
            ? (_datasourceId, name) => actionControls?.onRenameInput?.(item, name)
            : undefined)}
        onCopyAction={canManageAction ? actionControls?.onDuplicate : undefined}
        canDelete={canDeleteInput || (canManageAction && Boolean(actionMeta?.canDelete))}
        isInParallelGroup={canManageAction && Boolean(actionMeta?.isInParallelGroup)}
        groupMode={actionMeta?.groupMode || 'sequential'}
        isSearchActive={Boolean(search?.active)}
        isSearchMatch={Boolean(search?.match)}
        disableHover={disableHover || Boolean(pendingMutation)}
        dragHandleClassName={isAction ? 'dag-action-node-drag-handle' : ''}
        showWidgetBadge={false}
        forceScopeIndicatorFrame
        controlMenuTop={-30}
        idleMenuContent={(trackingLabel || String(actionControls?.trackingPendingId || '') === String(id)) && (
          <ActionTrackingBubble label={trackingLabel} count={actionMeta?.trackingCount} t={t}
            onOpenEvents={actionControls?.onOpenTrackingEvents
              ? () => actionControls.onOpenTrackingEvents(setting) : undefined}
            preparing={String(actionControls?.trackingPendingId || '') === String(id)}
            onRename={canManageAction && !actionControls?.trackingPendingId && actionControls?.onToggleTracking
              ? nextLabel => actionControls.onToggleTracking(setting, nextLabel) : undefined} />
        )}
        showNodeTooltip={isAction || kind === 'input'}
        styles={actionControlStyles}
      />
      {pendingMutation && (
        <DagActionMutationOverlay
          kind={pendingMutation}
          ariaLabel={pendingMutation === 'creating'
            ? t('dag.action_creating_aria', 'Saving action')
            : t('dag.action_deleting_aria', 'Deleting action')}
          label={pendingMutation === 'creating'
            ? t('dag.action_creating', 'Saving action…')
            : t('dag.action_deleting', 'Deleting action…')}
          t={t}
        />
      )}
      <NodeStatusIndicator
        label={label}
        error={error}
        runtime={runtime}
        t={t}
        disableHover={disableHover}
        onOpenJournal={() => actionControls?.onOpenJournal?.({
          nodeType: isAction ? 'action' : 'connector',
          nodeId: isAction ? id : item?.datasource?.id,
          label,
          journalMode: isAction ? 'node' : 'connector-node',
        })}
      />
    </Box>
  );
};

const UnfoldingNode = ({ id, data, dragging = false }) => {
  const isDraggingAction = useContext(DagInteractionContext);
  const seenEntranceNodeIds = useContext(DagEntranceContext);
  const internalsBatcher = useContext(DagNodeInternalsContext);
  const updateNodeInternals = useUpdateNodeInternals();
  const prefersReducedMotion = useReducedMotion();
  const buildHighlight = useContext(DagBuildFocusContext) === id;
  const reduceMotion = prefersReducedMotion || data.reduceEffects;
  const hasMountedBefore = seenEntranceNodeIds?.has(id);
  const entranceAnimationCompleted = useRef(false);
  const needsEntranceRefresh = useRef(!reduceMotion && !hasMountedBefore);
  const entranceDelay = reduceMotion
    ? 0
    : Math.min(Math.max(Number(data.animationIndex) || 0, 0) * 0.018, 0.34);
  const isProcessing = (
    resolveDagNodeStatus(data.error, data.runtime)?.state === 'processing'
    && data.runtime?.animate !== false
    && !reduceMotion
    && !dragging
    && !isDraggingAction
  );
  const anchorSignature = useMemo(() => {
    const target = data.node.handleAnchors?.target;
    const source = data.node.handleAnchors?.source;
    return [
      target?.x, target?.y, target?.position,
      source?.x, source?.y, source?.position,
      data.node.item?.type,
      data.node.templateSetupStatus?.completed,
      data.node.templateSetupStatus?.total,
    ].join(':');
  }, [data.node.handleAnchors, data.node.item?.type, data.node.templateSetupStatus]);

  useEffect(() => {
    seenEntranceNodeIds?.add(id);
  }, [id, seenEntranceNodeIds]);

  useEffect(() => {
    if (internalsBatcher) internalsBatcher.schedule(id, updateNodeInternals);
    else updateNodeInternals(id);
  }, [anchorSignature, id, internalsBatcher, updateNodeInternals]);

  return (
    <motion.div
      initial={reduceMotion || hasMountedBefore ? false : { opacity: 0, y: 12, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={!reduceMotion && !dragging && !isDraggingAction ? {
        y: -2,
        scale: 1.012,
        transition: { type: 'spring', stiffness: 430, damping: 24 },
      } : undefined}
      transition={{
        opacity: { duration: 0.28, ease: 'easeOut', delay: entranceDelay },
        y: { type: 'spring', stiffness: 330, damping: 24, delay: entranceDelay },
        scale: { type: 'spring', stiffness: 330, damping: 24, delay: entranceDelay },
      }}
      onAnimationComplete={() => {
        if (entranceAnimationCompleted.current || !needsEntranceRefresh.current) return;
        entranceAnimationCompleted.current = true;
        if (internalsBatcher) internalsBatcher.schedule(id, updateNodeInternals);
        else updateNodeInternals(id);
      }}
      style={{ width: data.node.width || NODE_WIDTH, transformOrigin: 'center center' }}
    >
    <Box
      data-project-dag-node="true"
      data-node-key={data.node.key}
      data-node-kind={data.node.kind}
      data-template-setup-current={data.node.templateSetupCurrent ? 'true' : 'false'}
      aria-current={data.node.templateSetupCurrent ? 'step' : undefined}
      sx={{
        position: 'relative',
        width: data.node.width || NODE_WIDTH,
        pb: data.node.templateSetupStatus ? 2 : 0,
        cursor: dragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        ...(data.node.templateSetupCurrent ? {
          outline: '3px solid #5757b0',
          outlineOffset: 6,
          borderRadius: 2,
          boxShadow: '0 0 0 10px rgba(87,87,176,.14)',
        } : {}),
        '&:active': { cursor: 'grabbing' },
        '& .dag-add-node-handle': {
          transition: 'width 160ms ease, height 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
        },
        '& .dag-add-node-icon': {
          opacity: 0,
          transform: 'scale(0.55)',
          transition: 'opacity 140ms ease, transform 160ms ease',
        },
        '& .dag-add-node-handle:hover, & .dag-add-node-handle:focus-visible': {
          width: 26,
          height: 26,
          backgroundColor: '#5050aa',
          boxShadow: '0 4px 12px rgba(55,55,120,.42)',
          outline: '3px solid rgba(101,101,189,.18)',
        },
        '& .dag-add-node-handle:hover .dag-add-node-icon, & .dag-add-node-handle:focus-visible .dag-add-node-icon': {
          opacity: 1,
          transform: 'scale(1)',
        },
      }}
    >
      {data.node.templateSetupCurrent && (
        <Chip
          size="small"
          color="primary"
          aria-label={data.t('template.setup_current_node', 'Current guide step')}
          label={data.t('template.setup_current_node', 'Current guide step')}
          sx={{
            position: 'absolute',
            left: '50%',
            top: -14,
            transform: 'translateX(-50%)',
            zIndex: 5,
            height: 21,
            fontSize: 10,
            fontWeight: 800,
            pointerEvents: 'none',
            border: '2px solid #fff',
            boxShadow: '0 2px 6px rgba(48,48,106,.22)',
          }}
        />
      )}
      <NodeHandles
        node={data.node}
        actionControls={data.actionControls}
        t={data.t}
        buildHighlight={buildHighlight}
        animateBuild={!reduceMotion && !dragging && !isDraggingAction}
      />
      <motion.div
        animate={isProcessing ? { scale: [1, 1.016, 1] } : { scale: 1 }}
        transition={isProcessing
          ? { scale: { duration: 2.1, ease: 'easeInOut', repeat: Infinity } }
          : { scale: { duration: 0.2, ease: 'easeOut' } }}
        style={{ width: '100%', transformOrigin: 'center center' }}
      >
        <DagNode
          id={data.node.id}
          label={data.node.label}
          kind={data.node.kind}
          reanalysis={data.node.reanalysis}
          iconSrc={data.node.iconSrc}
          isEnabled={data.node.isEnabled}
          item={data.node.item}
          connectorDisplayName={data.node.connectorDisplayName}
          actionMeta={data.node.actionMeta}
          actionControls={data.actionControls}
          search={data.search}
          error={data.error}
          runtime={data.runtime}
          inputLoading={data.node.inputLoading}
          outputLoading={data.node.outputLoading}
          flowState={data.node.flowState}
          actionCount={data.node.actionCount}
          flowCollapsed={data.node.flowCollapsed}
          tagCount={data.node.tagCount}
          receivedCount={data.node.receivedCount}
          disableHover={dragging || isDraggingAction}
          flowSummary={data.node.flowSummary}
          locale={data.locale}
          t={data.t}
        />
      </motion.div>
      {data.node.templateSetupStatus && (
        <Tooltip
          title={data.t(
            'template.setup_node_badge',
            '{{done}} of {{total}} setup steps confirmed',
            {
              done: data.node.templateSetupStatus.completed,
              total: data.node.templateSetupStatus.total,
            }
          )}
        >
          <Chip
            size="small"
            label={`${data.node.templateSetupStatus.completed}/${data.node.templateSetupStatus.total}`}
            color={data.node.templateSetupStatus.completed === data.node.templateSetupStatus.total
              ? 'success'
              : 'primary'}
            sx={{
              position: 'absolute',
              left: '50%',
              bottom: 0,
              transform: 'translateX(-50%)',
              height: 20,
              fontSize: 10,
              fontWeight: 800,
              pointerEvents: 'none',
              '& .MuiChip-label': { px: 0.8 },
            }}
          />
        </Tooltip>
      )}
    </Box>
    </motion.div>
  );
};

export default memo(UnfoldingNode);
